import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { dbCourseToApp } from '@/types/database-mappings';
import type { AppCourse } from '@/types/database-mappings';

// Extended course type with UI counts
export interface AdminCourse extends AppCourse {
  _count?: {
    modules: number;
    lessons: number;
  };
}

export const adminCourseKeys = {
  all: ['adminCourses'] as const,
  lists: () => [...adminCourseKeys.all, 'list'] as const,
  list: (filters: { courseType?: string; search?: string }) =>
    [...adminCourseKeys.lists(), filters] as const,
  detail: (id: string) => [...adminCourseKeys.all, 'detail', id] as const,
  stats: () => [...adminCourseKeys.all, 'stats'] as const,
};

// List courses with module/lesson counts, optional courseType filter
export function useAdminCourses(filters?: { courseType?: string; search?: string }) {
  const { user } = useAuth();

  return useQuery({
    queryKey: adminCourseKeys.list(filters ?? {}),
    queryFn: async () => {
      // Fetch all courses
      let query = supabase
        .from('courses')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.courseType && filters.courseType !== 'all') {
        query = query.eq('course_type', filters.courseType);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch modules with lesson counts
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select(`id, course_id, lessons(id)`);

      if (modulesError) throw modulesError;

      // Build counts per course
      const coursesWithCount: AdminCourse[] = data.map(course => {
        const courseModules = modulesData.filter(m => m.course_id === course.id);
        const moduleCount = courseModules.length;
        const lessonCount = courseModules.reduce(
          (total, m) => total + (m.lessons ? m.lessons.length : 0),
          0
        );

        return {
          ...dbCourseToApp(course),
          _count: { modules: moduleCount, lessons: lessonCount },
        };
      });

      // Client-side search filter
      if (filters?.search) {
        const q = filters.search.toLowerCase();
        return coursesWithCount.filter(c => c.title.toLowerCase().includes(q));
      }

      return coursesWithCount;
    },
    enabled: !!user,
  });
}

// Single course detail with modules + nested lessons
export function useAdminCourseDetail(
  courseId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  const { user } = useAuth();
  const { enabled = true } = options;

  return useQuery({
    queryKey: adminCourseKeys.detail(courseId ?? ''),
    queryFn: async () => {
      // Fetch course
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId!)
        .single();

      if (courseError) throw courseError;

      // Fetch modules
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select('*')
        .eq('course_id', courseId!)
        .order('position', { ascending: true });

      if (modulesError) throw modulesError;

      // Fetch lessons if there are modules
      let lessonsData: any[] = [];
      if (modulesData && modulesData.length > 0) {
        const moduleIds = modulesData.map(m => m.id);
        const { data: lessons, error: lessonsError } = await supabase
          .from('lessons')
          .select('*')
          .in('module_id', moduleIds)
          .order('position', { ascending: true });

        if (lessonsError) throw lessonsError;
        lessonsData = lessons || [];
      }

      // Fetch subject associations
      const { data: courseSubjects } = await supabase
        .from('course_subjects')
        .select('subject_id')
        .eq('course_id', courseId!);

      // Organize into nested structure
      const modules = (modulesData || []).map(module => ({
        ...module,
        lessons: lessonsData
          .filter(l => l.module_id === module.id)
          .map((lesson: any) => ({
            ...lesson,
            attachments: Array.isArray(lesson.attachments)
              ? lesson.attachments
              : typeof lesson.attachments === 'string'
                ? JSON.parse(lesson.attachments)
                : [],
            status: lesson.status || 'Draft',
          })),
        isExpanded: true,
      }));

      return {
        course: dbCourseToApp(courseData),
        modules,
        subjectIds: courseSubjects?.map(cs => cs.subject_id) || [],
      };
    },
    enabled: !!user && !!courseId && enabled,
  });
}

// Create course mutation
export function useCreateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (courseData: {
      title: string;
      overview?: string;
      tile_description?: string;
      days_of_access?: number;
      is_featured?: boolean;
      status?: string;
      what_youll_learn?: string[];
      course_type?: 'standard' | 'nextgen';
    }) => {
      const { data, error } = await supabase
        .from('courses')
        .insert({
          ...courseData,
          status: (courseData.status || 'Draft') as "Draft" | "Coming Soon" | "Published" | "Archived",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.all });
    },
  });
}

// Update course mutation
export function useUpdateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: {
      id: string;
      title?: string;
      overview?: string;
      tile_description?: string;
      days_of_access?: number;
      is_featured?: boolean;
      status?: string;
      what_youll_learn?: string[];
      course_type?: 'standard' | 'nextgen';
    }) => {
      const { error } = await supabase
        .from('courses')
        .update({
          ...updates,
          status: updates.status as "Draft" | "Coming Soon" | "Published" | "Archived" | undefined,
        })
        .eq('id', id);

      if (error) throw error;
      return { id };
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: adminCourseKeys.detail(updates.id) });
      await queryClient.cancelQueries({ queryKey: adminCourseKeys.lists() });

      const previousDetail = queryClient.getQueryData(adminCourseKeys.detail(updates.id));

      // Optimistic update on detail
      if (previousDetail) {
        queryClient.setQueryData(adminCourseKeys.detail(updates.id), (old: any) => ({
          ...old,
          course: { ...old.course, ...updates },
        }));
      }

      return { previousDetail };
    },
    onError: (_err, updates, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(adminCourseKeys.detail(updates.id), context.previousDetail);
      }
    },
    onSuccess: (_, updates) => {
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.detail(updates.id) });
    },
  });
}

// Delete course mutation (cascading: lessons -> modules -> course)
export function useDeleteCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (courseId: string) => {
      // Get module IDs for this course
      const { data: modules, error: modulesError } = await supabase
        .from('modules')
        .select('id')
        .eq('course_id', courseId);

      if (modulesError) throw modulesError;

      if (modules && modules.length > 0) {
        const moduleIds = modules.map(m => m.id);

        // Delete lessons for these modules
        const { error: lessonsError } = await supabase
          .from('lessons')
          .delete()
          .in('module_id', moduleIds);

        if (lessonsError) throw lessonsError;

        // Delete modules
        const { error: modulesDeleteError } = await supabase
          .from('modules')
          .delete()
          .eq('course_id', courseId);

        if (modulesDeleteError) throw modulesDeleteError;
      }

      // Delete course-subject associations
      await supabase
        .from('course_subjects')
        .delete()
        .eq('course_id', courseId);

      // Delete the course
      const { error: courseDeleteError } = await supabase
        .from('courses')
        .delete()
        .eq('id', courseId);

      if (courseDeleteError) throw courseDeleteError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.all });
    },
  });
}

// Aggregate stats
export function useAdminCourseStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: adminCourseKeys.stats(),
    queryFn: async () => {
      const [coursesRes, modulesRes, lessonsRes] = await Promise.all([
        supabase.from('courses').select('*', { count: 'exact', head: true }),
        supabase.from('modules').select('*', { count: 'exact', head: true }),
        supabase.from('lessons').select('*', { count: 'exact', head: true }),
      ]);

      if (coursesRes.error) throw coursesRes.error;
      if (modulesRes.error) throw modulesRes.error;
      if (lessonsRes.error) throw lessonsRes.error;

      return {
        courses: coursesRes.count || 0,
        modules: modulesRes.count || 0,
        lessons: lessonsRes.count || 0,
      };
    },
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
  });
}
